import { getAppRegion } from '../utils/getAppRegion';
import { Maybe } from '../utils/maybe';
import { AppRegion } from './appRegion';

export const TEST_TOAST = process.env.NODE_ENV === 'development' ?? false;

const defaultEnv = {
  BB_BRANDING: Maybe.from(process.env.BB_BRANDING).getValueOrThrow(
    'BB_BRANDING in required'
  ),
  BB_OPENID_CLIENT_ID: Maybe.from(
    process.env.BB_OPENID_CLIENT_ID
  ).getValueOrThrow('BB_OPENID_CLIENT_ID is required'),
  BB_OPENID_CLIENT_SECRET: Maybe.from(
    process.env.BB_OPENID_CLIENT_SECRET
  ).getValueOrThrow('BB_OPENID_CLIENT_SECRET is required'),
  ESSENTIALS_MICROFRONTEND_ORIGIN: Maybe.from(
    process.env.ESSENTIALS_MICROFRONTEND_ORIGIN
  ).getValueOrThrow('ESSENTIALS_MICROFRONTEND_ORIGIN is required'),
  FULLSTORY_ORGANIZATION_ID: Maybe.from(
    process.env.FULLSTORY_ORGANIZATION_ID
  ).getValueOrDefault(''),
  I18N_BASE_RESOURCE_URL: Maybe.from(
    process.env.I18N_BASE_RESOURCE_URL
  ).getValueOrDefault('https://cloudcdn.bluebeam.com/locales'),
  IGNORE_LICENSING_TIER:
    Maybe.from(process.env.IGNORE_LICENSING_TIER).getValueOrDefault('false') ===
    'true',
  LAUNCHDARKLY_CLIENT_SIDE_ID: Maybe.from(
    process.env.LAUNCHDARKLY_CLIENT_SIDE_ID
  ).getValueOrThrow('LAUNCHDARKLY_CLIENT_SIDE_ID is required'),
  MAPBOX_API_KEY: Maybe.from(process.env.MAPBOX_API_KEY).getValueOrThrow(
    'MAPBOX_API_KEY is required'
  ),
  OBSERVABILITY_ENV_NAME: Maybe.from(
    process.env.OBSERVABILITY_ENV_NAME
  ).getValueOrThrow('OBSERVABILITY_ENV_NAME is required'),
  PSPDFKIT_KEY: Maybe.from(process.env.PSPDFKIT_KEY).getValueOrThrow(
    'PSPDFKIT_KEY is required'
  ),
  SENTRY_DSN: Maybe.from(process.env.SENTRY_DSN).getValueOrThrow(
    'SENTRY_DSN is required'
  ),
  SENTRY_SAMPLE_RATE: (process.env.SENTRY_SAMPLE_RATE || 0.25) as number,
  STRIPE_PUBLISHABLE_KEY: Maybe.from(
    process.env.STRIPE_PUBLISHABLE_KEY
  ).getValueOrThrow('STRIPE_PUBLISHABLE_KEY is required'),
  TEST_TOAST: process.env.NODE_ENV === 'development',
  USE_REFRESH_TOKENS:
    Maybe.from(process.env.USE_REFRESH_TOKENS).getValueOrThrow(
      'USE_REFRESH_TOKENS is required'
    ) === 'true',
  US_SUBSCRIPTION_URL: Maybe.from(
    process.env.US_SUBSCRIPTION_URL
  ).getValueOrDefault(
    '/subscription?utm_source=bbcloud&utm_medium=subscription&utm_content=buttoncta&utm_term=q3&utm_campaign=bb-alt'
  ),
};

/**
 * A set of environment variables that are constant
 * regardless of the region in which the application
 * is running.
 */
type GlobalEnvironmentVariables = typeof defaultEnv;

const enUsEnv = {
  API_GATEWAY_CONTACT_US_ENDPOINT_02: Maybe.from(
    process.env.API_GATEWAY_CONTACT_US_ENDPOINT_02
  ).getValueOrThrow('API_GATEWAY_CONTACT_US_ENDPOINT is required'),
  API_GATEWAY_CONTACT_US_ENDPOINT: Maybe.from(
    process.env.API_GATEWAY_CONTACT_US_ENDPOINT
  ).getValueOrThrow('API_GATEWAY_CONTACT_US_ENDPOINT is required'),
  API_GATEWAY_ENDPOINT_02: Maybe.from(
    process.env.API_GATEWAY_ENDPOINT_02
  ).getValueOrThrow('API_GATEWAY_ENDPOINT is required'),
  API_GATEWAY_ENDPOINT: Maybe.from(
    process.env.API_GATEWAY_ENDPOINT
  ).getValueOrThrow('API_GATEWAY_ENDPOINT is required'),
  API_GATEWAY_GRAPHQL_ENDPOINT_02: Maybe.from(
    process.env.API_GATEWAY_GRAPHQL_ENDPOINT_02
  ).getValueOrThrow('API_GATEWAY_GRAPHQL_ENDPOINT is required'),
  API_GATEWAY_GRAPHQL_ENDPOINT: Maybe.from(
    process.env.API_GATEWAY_GRAPHQL_ENDPOINT
  ).getValueOrThrow('API_GATEWAY_GRAPHQL_ENDPOINT is required'),
  BB_OPENID_CREATE_USER_ENDPOINT: Maybe.from(
    process.env.BB_OPENID_CREATE_USER_ENDPOINT
  ).getValueOrThrow('BB_OPENID_CREATE_USER_ENDPOINT is required'),
  BB_OPENID_ENDPOINT: Maybe.from(
    process.env.BB_OPENID_ENDPOINT
  ).getValueOrThrow('BB_OPENID_ENDPOINT is required'),
  BBID_OKTA_ISSUER: Maybe.from(process.env.BBID_OKTA_ISSUER).getValueOrThrow(
    'BBID_OKTA_ISSUER is required'
  ),
  BROADCAST_SERVICE_ENDPOINT: Maybe.from(
    process.env.BROADCAST_SERVICE_ENDPOINT
  ).getValueOrThrow('BROADCAST_SERVICE_ENDPOINT is required'),
  OAUTH_AUTH_URL: Maybe.from(process.env.OAUTH_AUTH_URL).getValueOrThrow(
    'OAUTH_AUTH_URL is required'
  ),
  SENTRY_TRACING_ORIGINS: Maybe.from(
    process.env.SENTRY_TRACING_ORIGINS
  ).getValueOrThrow(
    'SENTRY_TRACING_ORIGINS is required'
  ) as unknown as string[],
  WEBSOCKET_ENDPOINT_02: Maybe.from(
    process.env.WEBSOCKET_ENDPOINT_02
  ).getValueOrThrow('WEBSOCKET_ENDPOINT is required'),
  WEBSOCKET_ENDPOINT: Maybe.from(
    process.env.WEBSOCKET_ENDPOINT
  ).getValueOrThrow('WEBSOCKET_ENDPOINT is required'),
};

/**
 * A set of environment variables that are different
 * based on the region in which the application
 * is running.
 */
type RegionSpecificEnvironmentVariables = typeof enUsEnv;

const auEnv: RegionSpecificEnvironmentVariables =
  getAppRegion() === AppRegion.AU
    ? {
        API_GATEWAY_CONTACT_US_ENDPOINT_02: Maybe.from(
          process.env.AU_API_GATEWAY_CONTACT_US_ENDPOINT_02
        ).getValueOrThrow('AU_API_GATEWAY_CONTACT_US_ENDPOINT is required'),
        API_GATEWAY_CONTACT_US_ENDPOINT: Maybe.from(
          process.env.AU_API_GATEWAY_CONTACT_US_ENDPOINT
        ).getValueOrThrow('AU_API_GATEWAY_CONTACT_US_ENDPOINT is required'),
        API_GATEWAY_ENDPOINT_02: Maybe.from(
          process.env.AU_API_GATEWAY_ENDPOINT_02
        ).getValueOrThrow('AU_API_GATEWAY_ENDPOINT is required'),
        API_GATEWAY_ENDPOINT: Maybe.from(
          process.env.AU_API_GATEWAY_ENDPOINT
        ).getValueOrThrow('AU_API_GATEWAY_ENDPOINT is required'),
        API_GATEWAY_GRAPHQL_ENDPOINT_02: Maybe.from(
          process.env.AU_API_GATEWAY_GRAPHQL_ENDPOINT_02
        ).getValueOrThrow('AU_API_GATEWAY_GRAPHQL_ENDPOINT is required'),
        API_GATEWAY_GRAPHQL_ENDPOINT: Maybe.from(
          process.env.AU_API_GATEWAY_GRAPHQL_ENDPOINT
        ).getValueOrThrow('AU_API_GATEWAY_GRAPHQL_ENDPOINT is required'),
        BB_OPENID_CREATE_USER_ENDPOINT: Maybe.from(
          process.env.AU_BB_OPENID_CREATE_USER_ENDPOINT
        ).getValueOrThrow('AU_BB_OPENID_CREATE_USER_ENDPOINT is required'),
        BB_OPENID_ENDPOINT: Maybe.from(
          process.env.AU_BB_OPENID_ENDPOINT
        ).getValueOrThrow('AU_BB_OPENID_ENDPOINT is required'),
        BBID_OKTA_ISSUER: Maybe.from(
          process.env.AU_BBID_OKTA_ISSUER
        ).getValueOrThrow('AU_BBID_OKTA_ISSUER is required'),
        BROADCAST_SERVICE_ENDPOINT: Maybe.from(
          process.env.AU_BROADCAST_SERVICE_ENDPOINT
        ).getValueOrThrow('AU_BROADCAST_SERVICE_ENDPOINT is required'),
        OAUTH_AUTH_URL: Maybe.from(
          process.env.AU_OAUTH_AUTH_URL
        ).getValueOrThrow('AU_OAUTH_AUTH_URL is required'),
        SENTRY_TRACING_ORIGINS: Maybe.from(
          process.env.AU_SENTRY_TRACING_ORIGINS
        ).getValueOrThrow(
          'AU_SENTRY_TRACING_ORIGINS is required'
        ) as unknown as string[],
        WEBSOCKET_ENDPOINT_02: Maybe.from(
          process.env.AU_WEBSOCKET_ENDPOINT_02
        ).getValueOrThrow('WEBSOCKET_ENDPOINT is required'),
        WEBSOCKET_ENDPOINT: Maybe.from(
          process.env.AU_WEBSOCKET_ENDPOINT
        ).getValueOrThrow('AU_WEBSOCKET_ENDPOINT is required'),
      }
    : enUsEnv;

const deEnv: RegionSpecificEnvironmentVariables =
  getAppRegion() === AppRegion.DE
    ? {
        API_GATEWAY_CONTACT_US_ENDPOINT_02: Maybe.from(
          process.env.DE_API_GATEWAY_CONTACT_US_ENDPOINT_02
        ).getValueOrThrow('DE_API_GATEWAY_CONTACT_US_ENDPOINT is required'),
        API_GATEWAY_CONTACT_US_ENDPOINT: Maybe.from(
          process.env.DE_API_GATEWAY_CONTACT_US_ENDPOINT
        ).getValueOrThrow('DE_API_GATEWAY_CONTACT_US_ENDPOINT is required'),
        API_GATEWAY_ENDPOINT_02: Maybe.from(
          process.env.DE_API_GATEWAY_ENDPOINT_02
        ).getValueOrThrow('DE_API_GATEWAY_ENDPOINT is required'),
        API_GATEWAY_ENDPOINT: Maybe.from(
          process.env.DE_API_GATEWAY_ENDPOINT
        ).getValueOrThrow('DE_API_GATEWAY_ENDPOINT is required'),
        API_GATEWAY_GRAPHQL_ENDPOINT_02: Maybe.from(
          process.env.DE_API_GATEWAY_GRAPHQL_ENDPOINT_02
        ).getValueOrThrow('DE_API_GATEWAY_GRAPHQL_ENDPOINT is required'),
        API_GATEWAY_GRAPHQL_ENDPOINT: Maybe.from(
          process.env.DE_API_GATEWAY_GRAPHQL_ENDPOINT
        ).getValueOrThrow('DE_API_GATEWAY_GRAPHQL_ENDPOINT is required'),
        BB_OPENID_CREATE_USER_ENDPOINT: Maybe.from(
          process.env.DE_BB_OPENID_CREATE_USER_ENDPOINT
        ).getValueOrThrow('DE_BB_OPENID_CREATE_USER_ENDPOINT is required'),
        BB_OPENID_ENDPOINT: Maybe.from(
          process.env.DE_BB_OPENID_ENDPOINT
        ).getValueOrThrow('DE_BB_OPENID_ENDPOINT is required'),
        BBID_OKTA_ISSUER: Maybe.from(
          process.env.DE_BBID_OKTA_ISSUER
        ).getValueOrThrow('DE_BBID_OKTA_ISSUER is required'),
        BROADCAST_SERVICE_ENDPOINT: Maybe.from(
          process.env.DE_BROADCAST_SERVICE_ENDPOINT
        ).getValueOrThrow('DE_BROADCAST_SERVICE_ENDPOINT is required'),
        OAUTH_AUTH_URL: Maybe.from(
          process.env.DE_OAUTH_AUTH_URL
        ).getValueOrThrow('DE_OAUTH_AUTH_URL is required'),
        SENTRY_TRACING_ORIGINS: Maybe.from(
          process.env.DE_SENTRY_TRACING_ORIGINS
        ).getValueOrThrow(
          'DE_SENTRY_TRACING_ORIGINS is required'
        ) as unknown as string[],
        WEBSOCKET_ENDPOINT_02: Maybe.from(
          process.env.DE_WEBSOCKET_ENDPOINT_02
        ).getValueOrThrow('WEBSOCKET_ENDPOINT is required'),
        WEBSOCKET_ENDPOINT: Maybe.from(
          process.env.DE_WEBSOCKET_ENDPOINT
        ).getValueOrThrow('DE_WEBSOCKET_ENDPOINT is required'),
      }
    : enUsEnv;

const seEnv: RegionSpecificEnvironmentVariables =
  getAppRegion() === AppRegion.SE
    ? {
        API_GATEWAY_CONTACT_US_ENDPOINT_02: Maybe.from(
          process.env.SE_API_GATEWAY_CONTACT_US_ENDPOINT_02
        ).getValueOrThrow('SE_API_GATEWAY_CONTACT_US_ENDPOINT is required'),
        API_GATEWAY_CONTACT_US_ENDPOINT: Maybe.from(
          process.env.SE_API_GATEWAY_CONTACT_US_ENDPOINT
        ).getValueOrThrow('SE_API_GATEWAY_CONTACT_US_ENDPOINT is required'),
        API_GATEWAY_ENDPOINT_02: Maybe.from(
          process.env.SE_API_GATEWAY_ENDPOINT_02
        ).getValueOrThrow('SE_API_GATEWAY_ENDPOINT is required'),
        API_GATEWAY_ENDPOINT: Maybe.from(
          process.env.SE_API_GATEWAY_ENDPOINT
        ).getValueOrThrow('SE_API_GATEWAY_ENDPOINT is required'),
        API_GATEWAY_GRAPHQL_ENDPOINT_02: Maybe.from(
          process.env.SE_API_GATEWAY_GRAPHQL_ENDPOINT_02
        ).getValueOrThrow('SE_API_GATEWAY_GRAPHQL_ENDPOINT is required'),
        API_GATEWAY_GRAPHQL_ENDPOINT: Maybe.from(
          process.env.SE_API_GATEWAY_GRAPHQL_ENDPOINT
        ).getValueOrThrow('SE_API_GATEWAY_GRAPHQL_ENDPOINT is required'),
        BB_OPENID_CREATE_USER_ENDPOINT: Maybe.from(
          process.env.SE_BB_OPENID_CREATE_USER_ENDPOINT
        ).getValueOrThrow('SE_BB_OPENID_CREATE_USER_ENDPOINT is required'),
        BB_OPENID_ENDPOINT: Maybe.from(
          process.env.SE_BB_OPENID_ENDPOINT
        ).getValueOrThrow('SE_BB_OPENID_ENDPOINT is required'),
        BBID_OKTA_ISSUER: Maybe.from(
          process.env.SE_BBID_OKTA_ISSUER
        ).getValueOrThrow('SE_BBID_OKTA_ISSUER is required'),
        BROADCAST_SERVICE_ENDPOINT: Maybe.from(
          process.env.SE_BROADCAST_SERVICE_ENDPOINT
        ).getValueOrThrow('SE_BROADCAST_SERVICE_ENDPOINT is required'),
        OAUTH_AUTH_URL: Maybe.from(
          process.env.SE_OAUTH_AUTH_URL
        ).getValueOrThrow('SE_OAUTH_AUTH_URL is required'),
        SENTRY_TRACING_ORIGINS: Maybe.from(
          process.env.SE_SENTRY_TRACING_ORIGINS
        ).getValueOrThrow(
          'SE_SENTRY_TRACING_ORIGINS is required'
        ) as unknown as string[],
        WEBSOCKET_ENDPOINT_02: Maybe.from(
          process.env.SE_WEBSOCKET_ENDPOINT_02
        ).getValueOrThrow('WEBSOCKET_ENDPOINT is required'),
        WEBSOCKET_ENDPOINT: Maybe.from(
          process.env.SE_WEBSOCKET_ENDPOINT
        ).getValueOrThrow('SE_WEBSOCKET_ENDPOINT is required'),
      }
    : enUsEnv;

const ukEnv: RegionSpecificEnvironmentVariables =
  getAppRegion() === AppRegion.UK
    ? {
        API_GATEWAY_CONTACT_US_ENDPOINT_02: Maybe.from(
          process.env.UK_API_GATEWAY_CONTACT_US_ENDPOINT_02
        ).getValueOrThrow('UK_API_GATEWAY_CONTACT_US_ENDPOINT is required'),
        API_GATEWAY_CONTACT_US_ENDPOINT: Maybe.from(
          process.env.UK_API_GATEWAY_CONTACT_US_ENDPOINT
        ).getValueOrThrow('UK_API_GATEWAY_CONTACT_US_ENDPOINT is required'),
        API_GATEWAY_ENDPOINT_02: Maybe.from(
          process.env.UK_API_GATEWAY_ENDPOINT_02
        ).getValueOrThrow('UK_API_GATEWAY_ENDPOINT is required'),
        API_GATEWAY_ENDPOINT: Maybe.from(
          process.env.UK_API_GATEWAY_ENDPOINT
        ).getValueOrThrow('UK_API_GATEWAY_ENDPOINT is required'),
        API_GATEWAY_GRAPHQL_ENDPOINT_02: Maybe.from(
          process.env.UK_API_GATEWAY_GRAPHQL_ENDPOINT_02
        ).getValueOrThrow('UK_API_GATEWAY_GRAPHQL_ENDPOINT is required'),
        API_GATEWAY_GRAPHQL_ENDPOINT: Maybe.from(
          process.env.UK_API_GATEWAY_GRAPHQL_ENDPOINT
        ).getValueOrThrow('UK_API_GATEWAY_GRAPHQL_ENDPOINT is required'),
        BB_OPENID_CREATE_USER_ENDPOINT: Maybe.from(
          process.env.UK_BB_OPENID_CREATE_USER_ENDPOINT
        ).getValueOrThrow('UK_BB_OPENID_CREATE_USER_ENDPOINT is required'),
        BB_OPENID_ENDPOINT: Maybe.from(
          process.env.UK_BB_OPENID_ENDPOINT
        ).getValueOrThrow('UK_BB_OPENID_ENDPOINT is required'),
        BBID_OKTA_ISSUER: Maybe.from(
          process.env.UK_BBID_OKTA_ISSUER
        ).getValueOrThrow('UK_BBID_OKTA_ISSUER is required'),
        BROADCAST_SERVICE_ENDPOINT: Maybe.from(
          process.env.UK_BROADCAST_SERVICE_ENDPOINT
        ).getValueOrThrow('UK_BROADCAST_SERVICE_ENDPOINT is required'),
        OAUTH_AUTH_URL: Maybe.from(
          process.env.UK_OAUTH_AUTH_URL
        ).getValueOrThrow('UK_OAUTH_AUTH_URL is required'),
        SENTRY_TRACING_ORIGINS: Maybe.from(
          process.env.UK_SENTRY_TRACING_ORIGINS
        ).getValueOrThrow(
          'UK_SENTRY_TRACING_ORIGINS is required'
        ) as unknown as string[],
        WEBSOCKET_ENDPOINT_02: Maybe.from(
          process.env.UK_WEBSOCKET_ENDPOINT_02
        ).getValueOrThrow('WEBSOCKET_ENDPOINT is required'),
        WEBSOCKET_ENDPOINT: Maybe.from(
          process.env.UK_WEBSOCKET_ENDPOINT
        ).getValueOrThrow('UK_WEBSOCKET_ENDPOINT is required'),
      }
    : enUsEnv;

const getRegionalEnvironmentVars = (): GlobalEnvironmentVariables &
  RegionSpecificEnvironmentVariables => {
  switch (getAppRegion()) {
    case AppRegion.AU:
      return { ...defaultEnv, ...auEnv };
    case AppRegion.DE:
      return { ...defaultEnv, ...deEnv };
    case AppRegion.SE:
      return { ...defaultEnv, ...seEnv };
    case AppRegion.UK:
      return { ...defaultEnv, ...ukEnv };
    default:
      return { ...defaultEnv, ...enUsEnv };
  }
};

export const environmentVars = getRegionalEnvironmentVars();
